import type {
  AdminListingRow,
  AdminNote,
  AdminOverview,
  AdminTransactionStats,
  AdminUserRow,
  AdminUserStats,
  AuthProviders,
  AuthRequestResponse,
  AuthVerifyResponse,
  BookingEvent,
  BookingMessage,
  BookingTransaction,
  CheckoutResponse,
  ConditionPhoto,
  Dispute,
  Listing,
  ListingInput,
  MeResponse,
  OnboardingInput,
  PayoutBalance,
  PublicUser,
  Quote,
  QuoteRequest,
  Review,
  ReviewsResponse,
  SearchFilters,
  SearchResponse,
  TrailerType,
  UploadedImageResponse,
} from '@shared/api-types.js';

const BASE = '/api';

class ApiError extends Error {
  status: number;
  code: string;
  constructor(message: string, status: number, code: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    body: body == null ? undefined : JSON.stringify(body),
    ...init,
  });

  const text = await res.text();
  const json = text ? safeParse(text) : null;

  if (!res.ok) {
    const code = (json && typeof json === 'object' && 'error' in json
      ? String((json as { error: unknown }).error)
      : null) ?? `http_${res.status}`;
    throw new ApiError(code, res.status, code);
  }

  return (json ?? ({} as T)) as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function uploadForm<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const text = await res.text();
  const json = text ? safeParse(text) : null;
  if (!res.ok) {
    const code = (json && typeof json === 'object' && 'error' in json
      ? String((json as { error: unknown }).error)
      : null) ?? `http_${res.status}`;
    throw new ApiError(code, res.status, code);
  }
  return (json ?? ({} as T)) as T;
}

export const api = {
  health: () => request<{ ok: boolean; db: string; uptime: number; now: string }>('GET', '/health'),
  auth: {
    me: () => request<MeResponse>('GET', '/auth/me'),
    providers: () => request<AuthProviders>('GET', '/auth/providers'),
    requestCode: (email: string) =>
      request<AuthRequestResponse>('POST', '/auth/request', { email }),
    verify: (input: { email?: string; code?: string; token?: string }) =>
      request<AuthVerifyResponse>('POST', '/auth/verify', input),
    logout: () => request<{ ok: true }>('POST', '/auth/logout'),
    completeOnboarding: (input: OnboardingInput) =>
      request<{ user: PublicUser }>('POST', '/auth/complete-onboarding', input),
  },
  search: {
    listings: (filters: SearchFilters) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) {
        if (v === undefined || v === null || v === '') continue;
        q.set(k, String(v));
      }
      return request<SearchResponse>('GET', `/search/listings?${q.toString()}`);
    },
  },
  listings: {
    mine: () => request<{ listings: Listing[] }>('GET', '/listings/mine'),
    list: (params: { type?: TrailerType; limit?: number; offset?: number } = {}) => {
      const q = new URLSearchParams();
      if (params.type) q.set('type', params.type);
      if (params.limit) q.set('limit', String(params.limit));
      if (params.offset) q.set('offset', String(params.offset));
      const suffix = q.toString() ? `?${q.toString()}` : '';
      return request<{ listings: Listing[] }>('GET', `/listings${suffix}`);
    },
    get: (idOrSlug: string) =>
      request<{ listing: Listing }>('GET', `/listings/${idOrSlug}`),
    create: (input: ListingInput) =>
      request<{ listing: Listing }>('POST', '/listings', input),
    update: (id: string, input: Partial<ListingInput>) =>
      request<{ listing: Listing }>('PATCH', `/listings/${id}`, input),
    remove: (id: string) => request<{ ok: true }>('DELETE', `/listings/${id}`),
    suggestPrice: (input: {
      trailerType: string;
      lengthFt?: number;
      weightCapacityLb?: number;
      yearBuilt?: number;
      city?: string;
      province?: string;
    }) =>
      request<{ suggestedDailyCents: number; reasoning: string; source: 'claude' | 'heuristic' }>(
        'POST',
        '/listings/suggest-price',
        input,
      ),
  },
  uploads: {
    listingImage: (file: File, listingId?: string) => {
      const form = new FormData();
      form.append('image', file);
      if (listingId) form.append('listingId', listingId);
      return uploadForm<UploadedImageResponse>('/uploads/listing-image', form);
    },
  },
  payouts: {
    balance: () => request<PayoutBalance>('GET', '/payouts/balance'),
    earnings: () =>
      request<{
        listings: { total: number; active: number };
        lifetime: {
          total: number;
          completed: number;
          cancelled: number;
          gmvCents: number;
          payoutCents: number;
        };
        last30: { total: number; gmvCents: number; payoutCents: number };
        rating: { avg: number; count: number };
        payouts: { lifetimeCents: number; pendingCents: number };
        balanceCents: number;
        eTransferEmail: string | null;
      }>('GET', '/payouts/earnings'),
    setEmail: (email: string) =>
      request<{ eTransferEmail: string }>('PUT', '/payouts/e-transfer-email', { email }),
    request: () => request<{ ok: true }>('POST', '/payouts/request'),
  },
  transactions: {
    quote: (input: QuoteRequest) =>
      request<{ quote: Quote }>('POST', '/transactions/quote', input),
    create: (input: QuoteRequest & { message?: string }) =>
      request<{
        transaction: BookingTransaction;
        requiresPayment: boolean;
        firstInstallmentCents: number;
        initialTransition: string;
      }>('POST', '/transactions', input),
    checkout: (id: string) =>
      request<CheckoutResponse>('POST', `/transactions/${id}/checkout`),
    changePlan: (
      id: string,
      input: {
        paymentPlan: 'full' | 'half_now_half_later' | 'custom' | 'defer_until_pickup';
        customInstallmentDates?: string[];
      },
    ) => request<{ quote: Quote }>('POST', `/transactions/${id}/change-plan`, input),
    mine: (role: 'renter' | 'owner' | 'both' = 'both') =>
      request<{ transactions: BookingTransaction[] }>(
        'GET',
        `/transactions/mine?role=${role}`,
      ),
    get: (id: string) =>
      request<{
        transaction: BookingTransaction;
        messages: BookingMessage[];
        events: BookingEvent[];
      }>('GET', `/transactions/${id}`),
    transition: (id: string, name: string, reason?: string) =>
      request<{ transaction: BookingTransaction }>(
        'POST',
        `/transactions/${id}/transitions/${name}`,
        { reason },
      ),
    sendMessage: (id: string, body: string) =>
      request<{ message: BookingMessage }>('POST', `/transactions/${id}/messages`, { body }),
    unreadCount: () =>
      request<{ count: number }>('GET', '/transactions/unread-count'),
    markRead: (id: string) =>
      request<{ ok: true }>('POST', `/transactions/${id}/mark-read`),
  },
  reviews: {
    forUser: (userId: string) =>
      request<ReviewsResponse>('GET', `/reviews/users/${userId}`),
    forTransaction: (txId: string) =>
      request<{ reviews: Review[] }>('GET', `/reviews/transactions/${txId}`),
    create: (txId: string, input: { rating: number; body: string }) =>
      request<{ review: Review }>('POST', `/reviews/transactions/${txId}`, input),
  },
  conditionPhotos: {
    list: (txId: string) =>
      request<{ photos: ConditionPhoto[] }>('GET', `/condition-photos/${txId}`),
    upload: async (txId: string, phase: 'pre' | 'post', files: File[]) => {
      const form = new FormData();
      form.append('phase', phase);
      for (const f of files) form.append('photos', f);
      const res = await fetch(`/api/condition-photos/${txId}`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) throw new Error(`upload_failed_${res.status}`);
      return (await res.json()) as { photos: ConditionPhoto[] };
    },
  },
  disputes: {
    open: (
      txId: string,
      input: { reason: string; requestedAmountCents?: number; severity?: 'L1' | 'L2' | 'L3' },
    ) => request<{ dispute: Dispute }>('POST', `/disputes/transactions/${txId}`, input),
    forTransaction: (txId: string) =>
      request<{ disputes: Dispute[] }>('GET', `/disputes/transactions/${txId}`),
    adminList: (state: 'open' | 'investigating' | 'resolved' | 'rejected' | 'all' = 'open') =>
      request<{ disputes: Array<Record<string, unknown>> }>('GET', `/disputes/admin?state=${state}`),
    resolve: (
      id: string,
      input: { award: 'resolved' | 'rejected'; awardedAmountCents?: number; resolutionNotes?: string },
    ) => request<{ dispute: Dispute }>('POST', `/disputes/${id}/resolve`, input),
  },
  admin: {
    overview: () => request<AdminOverview>('GET', '/admin/overview'),
    listings: (status?: string) =>
      request<{ listings: AdminListingRow[] }>(
        'GET',
        `/admin/listings${status ? `?status=${status}` : ''}`,
      ),
    setListingStatus: (id: string, status: string) =>
      request<{ listing: AdminListingRow }>('POST', `/admin/listings/${id}/status`, { status }),
    users: () => request<{ users: AdminUserRow[] }>('GET', '/admin/users'),
    user: (id: string) =>
      request<{ user: Record<string, unknown> }>('GET', `/admin/users/${id}`),
    userStats: (id: string) => request<AdminUserStats>('GET', `/admin/users/${id}/stats`),
    userNotes: (id: string) =>
      request<{ notes: AdminNote[] }>('GET', `/admin/users/${id}/notes`),
    addUserNote: (id: string, body: string) =>
      request<{ note: AdminNote }>('POST', `/admin/users/${id}/notes`, { body }),
    deleteUserNote: (id: string, noteId: string) =>
      request<{ ok: true }>('DELETE', `/admin/users/${id}/notes/${noteId}`),
    patchUser: (id: string, patch: Record<string, unknown>) =>
      request<{ user: Record<string, unknown> }>('PATCH', `/admin/users/${id}`, patch),
    setUserRoles: (id: string, roles: string[]) =>
      request<{ user: AdminUserRow }>('POST', `/admin/users/${id}/roles`, { roles }),
    setIdentityVerified: (id: string, verified: boolean) =>
      request<{ user: AdminUserRow }>('POST', `/admin/users/${id}/identity-verify`, {
        verified,
      }),
    banUser: (id: string, reason?: string) =>
      request<{ user: AdminUserRow }>('POST', `/admin/users/${id}/ban`, { reason }),
    unbanUser: (id: string) =>
      request<{ user: AdminUserRow }>('POST', `/admin/users/${id}/unban`),
    forceTransition: (txId: string, transition: string, reason?: string) =>
      request<{ transaction: BookingTransaction }>(
        'POST',
        `/admin/transactions/${txId}/force-transition`,
        { transition, reason },
      ),
    transaction: (id: string) =>
      request<{ transaction: Record<string, unknown> }>('GET', `/admin/transactions/${id}`),
    transactionStats: (id: string) =>
      request<AdminTransactionStats>('GET', `/admin/transactions/${id}/stats`),
    transactionNotes: (id: string) =>
      request<{ notes: AdminNote[] }>('GET', `/admin/transactions/${id}/notes`),
    addTransactionNote: (id: string, body: string) =>
      request<{ note: AdminNote }>('POST', `/admin/transactions/${id}/notes`, { body }),
    deleteTransactionNote: (id: string, noteId: string) =>
      request<{ ok: true }>('DELETE', `/admin/transactions/${id}/notes/${noteId}`),
    patchTransaction: (id: string, patch: Record<string, unknown>) =>
      request<{ transaction: Record<string, unknown> }>(
        'PATCH',
        `/admin/transactions/${id}`,
        patch,
      ),
    jobs: () => request<{ jobs: Array<{ name: string; schedule: string }> }>('GET', '/admin/jobs'),
    runJob: (name: string) =>
      request<{ ok: true; name: string; count: number; ms: number }>(
        'POST',
        `/admin/jobs/${name}/run`,
      ),
  },
  identity: {
    status: () =>
      request<{ verified: boolean; verifiedAt: string | null }>(
        'GET',
        '/identity/status',
      ),
    session: () =>
      request<{ sessionId?: string; clientSecret?: string; url?: string; alreadyVerified?: boolean }>(
        'POST',
        '/identity/session',
      ),
  },
};

export { ApiError };
